import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Form, Button, Card, Container, Alert } from 'react-bootstrap';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { login, clearError } from '../../redux/slices/authSlice';
import styles from './Auth.module.css';

const validationSchema = Yup.object({
  email: Yup.string()
    .email('כתובת דואר אלקטרוני לא תקינה')
    .required('שדה חובה'),
  password: Yup.string()
    .min(6, 'הסיסמה חייבת להכיל לפחות 8 תווים')
    .required('שדה חובה')
});

const Login = ({ location = {} }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, token } = useSelector(state => state.auth);
  const [showPassword, setShowPassword] = useState(false);
  const redirect = location.search ? location.search.split('=')[1] : '/dashboard';

  useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (token) {
      navigate(redirect);
    }
  }, [token, navigate, redirect]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      // Clear any previous errors before submitting
      dispatch(clearError());
      const result = dispatch(login(values));
      if (!result.error) {
        navigate(redirect);
      }
    }
  });

  // Clear error when user starts typing
  useEffect(() => {
    if (error && (formik.values.email || formik.values.password)) {
      dispatch(clearError());
    }
  }, [formik.values.email, formik.values.password, error, dispatch]);

  return (
    <Container className={styles.authContainer}>
      <Card className={styles.authCard}>
        <Card.Body>
          <h2 className={styles.authTitle}>התחברות</h2>

          <Form onSubmit={formik.handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>דואר אלקטרוני</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.email && formik.errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>סיסמה</Form.Label>
              <div className={styles.passwordInputWrapper}>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.password && formik.errors.password}
                  className={styles.formControl}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              <Form.Control.Feedback type="invalid">
                {formik.errors.password}
              </Form.Control.Feedback>
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100 mb-3"
              disabled={loading}
            >
              {loading ? 'מתחבר...' : 'התחבר'}
            </Button>

            <div className={styles.authLinks}>
              <Link to="/forgot-password" className="text-primary">שכחת סיסמה?</Link>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Login; 